import { useMemo, useCallback, memo } from "react";
import Table, { TableRow } from "../../components/common/Table/Table";
import CardTable from "../../components/common/CardTable";
import Dropdown from "../../components/common/Dropdown";
import TextAreaWithCounter from "../../components/common/TextAreaWithCounter";
import RatingTag from "./components/RatingTag";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import type { RootState } from "../../store/store";
import {
  ActionItemData,
  actionItemColumns,
  setHasUnsavedChanges,
  setValidationError,
  setActionNeededEdited,
  updateActionItem,
  type DropdownOption,
  type CategoryActionItem,
  transformCategoryItemsToDropdownOptions,
} from "../../store/slices/pathToPerformanceSlice";
import { getCategoryTypeCode } from "../../utils/PathToPerformance/pathToPerformanceHelpers";
import { hasActionItemsChanged } from "../../utils/PathToPerformance/p2pValidation";
import { 
  DEFAULT_ACTION_OPTION, 
  MAX_NOTES_LENGTH,
  FIELD_IDS,
  ARIA_LABELS,
} from "../../constants/pathToPerformance";

interface ActionItemTableProps {
  data: ActionItemData[];
  readOnly?: boolean;
}

const ActionItemTable = memo<ActionItemTableProps>(({
  data,
  readOnly = false,
}) => {
  const dispatch = useAppDispatch();
  const { categoryLevels, initialActionItems } = useAppSelector((state: RootState) => state.pathToPerformance);

  /**
   * Build dropdown options from category levels API data
   * Memoized to avoid recreation on every render
   */
  const buildDropdownOptions = useCallback((catType: string): DropdownOption[] => {
    if (!categoryLevels) {
      return [{ id: 0, label: DEFAULT_ACTION_OPTION, value: "select" }];
    }

    const categoryItems = categoryLevels[catType as keyof typeof categoryLevels];
    if (!categoryItems) {
      return [{ id: 0, label: DEFAULT_ACTION_OPTION, value: "select" }];
    }

    return transformCategoryItemsToDropdownOptions(categoryItems);
  }, [categoryLevels]);

  /**
   * Handle item update - directly update Redux state
   * Only sets hasUnsavedChanges if the new value differs from initial data
   * useCallback ensures stable reference for memoization
   */
  const handleItemUpdate = useCallback((id: string, updates: Partial<ActionItemData>) => {
    dispatch(updateActionItem({ id, data: updates }));

    if (updates.actionNeeded !== undefined) {
      dispatch(setActionNeededEdited(true));
    }
    
    // Clear validation error when user makes a valid selection
    if (updates.actionNeeded && updates.actionNeeded !== DEFAULT_ACTION_OPTION) {
      dispatch(setValidationError(null));
    }
    
    // Create updated action items array to check for changes
    const updatedActionItems = data.map(item => 
      item.id === id ? { ...item, ...updates } : item
    );
    
    // Only set hasUnsavedChanges if there are actual changes from initial data
    const hasChanges = hasActionItemsChanged(updatedActionItems, initialActionItems);
    dispatch(setHasUnsavedChanges(hasChanges));
  }, [dispatch, data, initialActionItems]);

  /**
   * Handle dropdown selection for action needed
   * useCallback ensures stable reference for memoization
   */
  const handleActionSelect = useCallback((
    rowId: string, 
    catType: string, 
    option: DropdownOption | null
  ) => {
    if (!option || !categoryLevels) return;

    const categoryItems = categoryLevels[catType as keyof typeof categoryLevels];
    const selectedAction = categoryItems?.find(
      (item: CategoryActionItem) => item.actionNeededId === option.id
    );

    if (selectedAction) {
      // Update with category level (catgLvl) and action needed text
      handleItemUpdate(rowId, {
        actionNeeded: option.label,
        categoryLevel: selectedAction.catgLvl,
      });
    } else if (option.label === DEFAULT_ACTION_OPTION) {
      // Reset to default
      handleItemUpdate(rowId, {
        actionNeeded: DEFAULT_ACTION_OPTION,
        categoryLevel: 0,
      });
    }
  }, [categoryLevels, handleItemUpdate]);

  /**
   * Create stable handler for notes change
   * Prevents inline function creation in JSX
   */
  const createNotesChangeHandler = useCallback((rowId: string) => (value: string) => {
    handleItemUpdate(rowId, { notes: value });
  }, [handleItemUpdate]);

  /**
   * Transform data to table rows
   * Memoized with all dependencies to prevent unnecessary recalculation
   */
  const rows: TableRow[] = useMemo(
    () =>
      data.map((row) => {
        // Get category type code using centralized helper
        const catType = getCategoryTypeCode(row.category) ??  "";
        
        // Get action options for this category
        const actionOptions = buildDropdownOptions(catType);
        
        // Get rating (categoryLevel) - use type guard for safety
        const rating = typeof row.categoryLevel === "number" 
          ? row.categoryLevel 
          : Number.parseInt(String(row.categoryLevel), 10) || 0;

        return {
          category: row.category,
          categoryLevel: rating > 0 ? (
            <RatingTag rating={rating} />
          ) : (
            <span className="text-gray-400">-</span>
          ),
          actionNeeded: readOnly ? (
            <p className="text-sm p3 -medium">
              {row.actionNeeded && row.actionNeeded !== DEFAULT_ACTION_OPTION
                ? row.actionNeeded 
                : "-"}
            </p>
          ) : (
            <div className="relative flex flex-col gap-2 min-w-0 w-full">
              <Dropdown
                label="Action Needed"
                selectedValue={
                  actionOptions.find((opt) => opt.label === row.actionNeeded) ||
                  actionOptions[0]
                }
                options={actionOptions}
                onSelect={(option) => handleActionSelect(row.id, catType, option)}
                placeholder="Select"
                buttonClassName="py-0! w-full! px-4!"
                menuClassName="absolute z-10 bg-white w-full! max-h-80! overflow-y-auto!"
                aria-label={`Select action needed for ${row.category}`}
              />
            </div>
          ),
          notes: (
            <TextAreaWithCounter
              className="text-[16px]!"
              id={FIELD_IDS.ACTION_NOTES(row.id)}
              label="Notes"
              value={row.notes}
              placeholder="Enter notes"
              maxLength={MAX_NOTES_LENGTH}
              ariaLabel={ARIA_LABELS.NOTES_TEXTAREA}
              onChange={createNotesChangeHandler(row.id)}
              readOnly={readOnly}
            />
          ),
          id: row.id,
        };
      }),
    [data, readOnly, buildDropdownOptions, handleActionSelect, createNotesChangeHandler]
  );

  return (
    <div className="w-full pt-9!">
      <CardTable>
        {rows.length > 0 ? (
          <Table columns={actionItemColumns} rows={rows} className="table-fixed w-full" />
        ) : (
          <p className="p3 -book text-center mt-2.5! desktop:mt-50!">
            No action items found.
          </p>
        )}
      </CardTable>
    </div>
  );
});

ActionItemTable.displayName = "ActionItemTable";

export default ActionItemTable;
